import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  navigation: React.ReactNode;
  header: React.ReactNode;
  isCompact?: boolean;
  showMobileMenu: boolean;
  setShowMobileMenu: (show: boolean) => void;
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  navigation,
  header,
  isCompact = false,
  showMobileMenu,
  setShowMobileMenu
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showMobileMenu, setShowMobileMenu]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      {/* Mobile Menu Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 touch-target"
          aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
        >
          {showMobileMenu ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      {navigation}

      {/* Main Content Area */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          isCompact 
            ? 'lg:ml-16' 
            : 'lg:ml-64'
        } ${
          showMobileMenu 
            ? 'ml-0' 
            : 'ml-0'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-30">
          {header}
        </div>

        {/* Main Content */}
        <main className="p-responsive">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {showMobileMenu && isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// Responsive container utilities
export const ResponsiveContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full max-w-7xl mx-auto px-responsive">
    {children}
  </div>
);

// Responsive grid utilities
export const ResponsiveGrid: React.FC<{ 
  children: React.ReactNode;
  cols?: number;
  gap?: string;
}> = ({ children, cols = 1, gap = 'gap-4' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} ${gap}`}>
    {children}
  </div>
);

// Responsive card component
export const ResponsiveCard: React.FC<{ 
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-responsive ${className}`}>
    {children}
  </div>
);

// Responsive section component
export const ResponsiveSection: React.FC<{ 
  children: React.ReactNode;
  className?: string;
  title?: string;
}> = ({ children, className = '', title }) => (
  <section className={`py-responsive ${className}`}>
    {title && (
      <h2 className="text-responsive-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
        {title}
      </h2>
    )}
    {children}
  </section>
); 